import { useEffect, useRef, useState } from 'react';

const MIN_TRANSITION_MS = 220;

type GatewayRouteTransitionOptions = {
    routeWebsite?: string;
    activeWebsite?: string;
    minDuration?: number;
};

/**
 * Keeps a short transition skeleton visible while the route is moving from one
 * gateway context to another. This masks the sidebar/content swap and prevents
 * the user from seeing stale data from the previous gateway for a single frame.
 */
const useGatewayRouteTransition = ({
    routeWebsite,
    activeWebsite,
    minDuration = MIN_TRANSITION_MS,
}: GatewayRouteTransitionOptions) => {
    const [visible, setVisible] = useState(false);
    const startedAt = useRef(0);

    useEffect(() => {
        if (!routeWebsite) {
            startedAt.current = 0;
            setVisible(false);
            return;
        }

        const contextMismatch = activeWebsite !== routeWebsite;

        if (contextMismatch) {
            if (!visible) {
                startedAt.current = Date.now();
                setVisible(true);
            }
            return;
        }

        if (!visible) return;

        const elapsed = Date.now() - startedAt.current;
        const remaining = Math.max(0, minDuration - elapsed);
        const timeoutId = window.setTimeout(() => {
            setVisible(false);
            startedAt.current = 0;
        }, remaining);

        return () => window.clearTimeout(timeoutId);
    }, [activeWebsite, minDuration, routeWebsite, visible]);

    const contextMismatch = Boolean(
        routeWebsite && activeWebsite !== routeWebsite,
    );

    return visible || contextMismatch;
};

export default useGatewayRouteTransition;
